import React, { useEffect, useState, useRef, useCallback } from "react";

export interface SortableCardListProps<T extends { id: string }> {
  items: T[];
  renderCard: (item: T, index: number) => React.ReactNode;
  onReorder?: (ids: string[]) => void | Promise<void>;
  disabled?: boolean;
  className?: string;
  /** Number of columns for grid layout (e.g. 2 for two columns). Omit for flex wrap. */
  gridCols?: number;
}

export function SortableCardList<T extends { id: string }>({
  items,
  renderCard,
  onReorder,
  disabled = false,
  className,
  gridCols,
}: SortableCardListProps<T>) {
  const [internal, setInternal] = useState(items);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [animatingId, setAnimatingId] = useState<string | null>(null);
  const [animationOffset, setAnimationOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollIntervalRef = useRef<number | null>(null);
  const lastMouseY = useRef<number>(0);
  const cardRefsMap = useRef<Map<string, DOMRect>>(new Map());

  useEffect(() => setInternal(items), [items]);

  // Store card positions before swap
  const capturePositions = () => {
    if (!containerRef.current) return;
    const cards = containerRef.current.querySelectorAll('[data-card]');
    cards.forEach((card, index) => {
      const id = internal[index]?.id;
      if (id) {
        cardRefsMap.current.set(id, card.getBoundingClientRect());
      }
    });
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    if (disabled) return;
    setDragIndex(index);
    capturePositions();
    
    const target = e.currentTarget;
    const clone = target.cloneNode(true) as HTMLElement;
    clone.style.position = 'absolute';
    clone.style.top = '-9999px';
    clone.style.transform = 'rotate(3deg) scale(1.05)';
    clone.style.opacity = '0.95';
    clone.style.boxShadow = '0 20px 40px rgba(0,0,0,0.3)';
    clone.style.borderRadius = '0.5rem';
    document.body.appendChild(clone);
    
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setDragImage(clone, clone.offsetWidth / 2, clone.offsetHeight / 2);
    
    setTimeout(() => {
      if (clone.parentNode) {
        document.body.removeChild(clone);
      }
    }, 0);
  };

  const autoScroll = useCallback(() => {
    if (!containerRef.current) return;

    const mouseY = lastMouseY.current;
    const scrollThreshold = 150;
    const scrollSpeed = 15;

    let scrollContainer: HTMLElement | null = containerRef.current;
    while (scrollContainer && scrollContainer !== document.body) {
      const style = window.getComputedStyle(scrollContainer);
      if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && 
          scrollContainer.scrollHeight > scrollContainer.clientHeight) {
        break;
      }
      scrollContainer = scrollContainer.parentElement;
    }

    const useWindow = !scrollContainer || scrollContainer === document.body;
    
    if (useWindow) {
      const distanceFromTop = mouseY;
      const distanceFromBottom = window.innerHeight - mouseY;

      if (distanceFromTop < scrollThreshold && distanceFromTop > 0) {
        const speed = scrollSpeed * (1 - distanceFromTop / scrollThreshold);
        window.scrollBy(0, -speed);
      } else if (distanceFromBottom < scrollThreshold && distanceFromBottom > 0) {
        const speed = scrollSpeed * (1 - distanceFromBottom / scrollThreshold);
        window.scrollBy(0, speed);
      }
    } else if (scrollContainer) {
      const rect = scrollContainer.getBoundingClientRect();
      const distanceFromTop = mouseY - rect.top;
      const distanceFromBottom = rect.bottom - mouseY;

      if (distanceFromTop < scrollThreshold && distanceFromTop > 0 && scrollContainer.scrollTop > 0) {
        const speed = scrollSpeed * (1 - distanceFromTop / scrollThreshold);
        scrollContainer.scrollTop -= speed;
      } else if (distanceFromBottom < scrollThreshold && distanceFromBottom > 0) {
        const speed = scrollSpeed * (1 - distanceFromBottom / scrollThreshold);
        scrollContainer.scrollTop += speed;
      }
    }
  }, []);

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled || dragIndex === null) return;
    
    if (e.clientY !== 0) {
      lastMouseY.current = e.clientY;
    }

    if (!scrollIntervalRef.current) {
      scrollIntervalRef.current = window.setInterval(autoScroll, 16);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    if (disabled || dragIndex === null) return;
    e.preventDefault();
    e.stopPropagation();
    
    if (index !== hoverIndex && index !== dragIndex) {
      setHoverIndex(index);
    }
  };

  const handleDragEnter = (index: number) => {
    if (disabled || dragIndex === null) return;
    if (index !== dragIndex) {
      setHoverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setHoverIndex(null);
  };

  const handleDrop = async (dropIndex: number, e: React.DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (disabled || dragIndex === null || dragIndex === dropIndex) {
      cleanupDrag();
      return;
    }

    // Get old position of the card that will be swapped
    const swappedCardId = internal[dropIndex].id;
    const oldRect = cardRefsMap.current.get(swappedCardId);

    // Perform the swap
    const newItems = [...internal];
    [newItems[dragIndex], newItems[dropIndex]] = [newItems[dropIndex], newItems[dragIndex]];
    setInternal(newItems);

    // Calculate animation after DOM updates
    setTimeout(() => {
      if (!containerRef.current || !oldRect) return;
      
      const cards = containerRef.current.querySelectorAll('[data-card]');
      const newIndex = newItems.findIndex(item => item.id === swappedCardId);
      
      if (newIndex >= 0 && cards[newIndex]) {
        const newRect = cards[newIndex].getBoundingClientRect();
        const deltaX = oldRect.left - newRect.left;
        const deltaY = oldRect.top - newRect.top;
        
        if (Math.abs(deltaX) > 1 || Math.abs(deltaY) > 1) {
          setAnimatingId(swappedCardId);
          setAnimationOffset({ x: deltaX, y: deltaY });
          
          // Reset animation after it completes
          setTimeout(() => {
            setAnimatingId(null);
            setAnimationOffset({ x: 0, y: 0 });
          }, 400);
        }
      }
    }, 0);

    cleanupDrag();

    if (onReorder) {
      try {
        await onReorder(newItems.map((i) => i.id));
      } catch (err) {
        console.error(err);
        setInternal(internal);
      }
    }
  };

  const handleContainerDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled || dragIndex === null) {
      cleanupDrag();
      return;
    }

    const target = e.target as HTMLElement;
    const clickedCard = target.closest('[data-card]');
    
    if (!clickedCard) {
      e.preventDefault();
      
      const newItems = [...internal];
      const [draggedItem] = newItems.splice(dragIndex, 1);
      newItems.push(draggedItem);
      setInternal(newItems);
      cleanupDrag();

      if (onReorder) {
        try {
          await onReorder(newItems.map((i) => i.id));
        } catch (err) {
          console.error(err);
          setInternal(internal);
        }
      }
    } else {
      cleanupDrag();
    }
  };

  const cleanupDrag = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    setDragIndex(null);
    setHoverIndex(null);
    lastMouseY.current = 0;
  };

  const handleDragEnd = () => {
    cleanupDrag();
  };

  useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
  }, []);

  const getCardStyle = (item: T, index: number) => {
    const isDragging = dragIndex === index;
    const isHovered = hoverIndex === index;
    const isAnimating = animatingId === item.id;
    
    let transform = 'scale(1) rotate(0deg)';
    let transition = 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)';
    
    if (isDragging) {
      transform = 'scale(1.05) rotate(2deg)';
      transition = 'opacity 0.15s ease-out';
    } else if (isAnimating) {
      // Start from old position
      transform = `translate(${animationOffset.x}px, ${animationOffset.y}px) scale(1)`;
      transition = 'none';
      
      // Animate to new position
      requestAnimationFrame(() => {
        const element = document.querySelector(`[data-card-id="${item.id}"]`) as HTMLElement;
        if (element) {
          element.style.transform = 'translate(0, 0) scale(1)';
          element.style.transition = 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
        }
      });
    } else if (isHovered && dragIndex !== null && dragIndex !== index) {
      transform = 'scale(0.95)';
    }
    
    return {
      cursor: disabled ? "default" : isDragging ? "grabbing" : "grab",
      opacity: isDragging ? 0.4 : 1,
      transform,
      transition,
      filter: isDragging ? 'brightness(1.1)' : 'brightness(1)',
    };
  };

  const layoutClass = gridCols != null
    ? `grid gap-4 min-h-[150px]`
    : "flex flex-wrap";
  const gridStyle = gridCols != null
    ? { gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`, minHeight: '150px' }
    : { gap: '1.25rem', minHeight: '150px' };

  return (
    <div 
      ref={containerRef}
      data-sortable-container
      className={`${layoutClass} ${className ?? ""}`}
      style={gridStyle}
      onDrop={handleContainerDrop}
      onDragOver={(e) => e.preventDefault()}
    >
      {internal.map((item, index) => {
        const isDragging = dragIndex === index;
        const isHovered = hoverIndex === index && dragIndex !== null && dragIndex !== index;
        
        return (
          <div
            key={item.id}
            data-card
            data-card-id={item.id}
            draggable={!disabled}
            onDragStart={(e) => handleDragStart(e, index)}
            onDrag={handleDrag}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnter={() => handleDragEnter(index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(index, e)}
            onDragEnd={handleDragEnd}
            className="select-none relative"
            style={getCardStyle(item, index)}
          >
            {/* Swap indicator */}
            {isHovered && (
              <div 
                className="absolute inset-0 rounded-lg pointer-events-none"
                style={{
                  border: '3px dashed #3b82f6',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            )}
            
            {isDragging && (
              <div 
                className="absolute inset-0 rounded-lg pointer-events-none"
                style={{
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                }}
              />
            )}
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              {renderCard(item, index)}
            </div>
          </div>
        );
      })}
      
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
}

// Demo Component
function Demo() {
  const [cards, setCards] = useState([
    { id: '1', title: 'Card 1', color: 'bg-blue-500' },
    { id: '2', title: 'Card 2', color: 'bg-purple-500' },
    { id: '3', title: 'Card 3', color: 'bg-pink-500' },
    { id: '4', title: 'Card 4', color: 'bg-orange-500' },
    { id: '5', title: 'Card 5', color: 'bg-green-500' },
    { id: '6', title: 'Card 6', color: 'bg-red-500' },
  ]);

  const handleReorder = (ids: string[]) => {
    const newCards = ids.map(id => cards.find(c => c.id === id)!);
    setCards(newCards);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            Smooth Swap Animation
          </h1>
          <p className="text-gray-400">
            Drag cards to swap - watch the swapped card animate to its new position!
          </p>
        </div>

        <SortableCardList
          items={cards}
          onReorder={handleReorder}
          renderCard={(item) => (
            <div
              className={`${item.color} w-48 h-32 rounded-lg shadow-lg flex items-center justify-center text-white text-xl font-bold hover:shadow-2xl transition-shadow duration-300`}
            >
              {item.title}
            </div>
          )}
        />
      </div>
    </div>
  );
}

export default Demo;